import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Clapperboard, Film, Sparkles, Code2, Smartphone, PenTool,
  Layers, Share2, TrendingUp, ArrowUpRight
} from "lucide-react";
import { services } from "../data/mock";

const ICONS = { Clapperboard, Film, Sparkles, Code2, Smartphone, PenTool, Layers, Share2, TrendingUp };
const CATEGORIES = ["All", "Film & Video", "Technology", "Brand & Growth"];

const Services = () => {
  const [active, setActive] = useState("All");
  const filtered = active === "All" ? services : services.filter((s) => s.category === active);

  return (
    <section id="services" className="section relative">
      <div className="container-xl">
        <div className="flex items-end justify-between flex-wrap gap-8">
          <div>
            <Kicker index="01" label="Our Services" />
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="mt-5 font-display text-[clamp(40px,6vw,88px)] leading-[1.02] text-[var(--cream)] balance"
            >
              Creative thinking. <span className="italic gold-text">Strategic execution.</span><br />
              Zero compromise.
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="max-w-lg text-[15px] text-[var(--cream-muted)] leading-relaxed"
          >
            From brand identities and campaigns to films, content, websites, and digital experiences, every project is crafted with equal focus on creativity, clarity, and measurable impact.
          </motion.p>
        </div>

        {/* Filter tabs */}
        <div className="mt-14 flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              className={`btn-toggle ${active === c ? "active" : ""}`}
              data-cursor={c.toLowerCase()}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {filtered.map((s, i) => {
              const Icon = ICONS[s.iconName];
              const isBranding = s.title === "Branding";
              
              const CardContent = (
                <motion.article
                  key={s.id}
                  layout
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                  className="shine group relative p-8 md:p-10 bg-[var(--ink-2)] border border-[var(--rule)] rounded-2xl hover:border-[var(--gold)] transition-all duration-500 cursor-hover overflow-hidden h-full"
                  style={{ minHeight: 320 }}
                  data-cursor="view"
                >
                  {/* Background Image — Always visible but muted by default */}
                  <div 
                    className="absolute inset-0 opacity-30 group-hover:opacity-60 transition-opacity duration-700 pointer-events-none"
                    style={{
                      backgroundImage: `url(${s.image})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      transform: "scale(1.05)",
                    }}
                  />
                  {/* Subtle dark gradient overlay to ensure text is always readable */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)] via-[rgba(10,9,8,0.7)] to-[rgba(10,9,8,0.4)] opacity-80 group-hover:opacity-60 transition-opacity duration-500" />

                  <div className="relative z-10 h-full flex flex-col">
                    <div className="flex items-start justify-between">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center"
                        style={{ border: "1px solid rgba(201,169,97,0.35)", background: "rgba(201,169,97,0.05)" }}>
                        {Icon ? <Icon size={20} color="#e4c77a" strokeWidth={1.5} /> : null}
                      </div>
                      <span className="font-impact text-[10px] tracking-[0.3em] text-[var(--muted)]">
                        {s.category.toUpperCase()}
                      </span>
                    </div>
                    
                    <h3 className="mt-10 font-display text-[28px] leading-tight text-[var(--cream)] group-hover:text-[var(--gold)] transition-colors duration-300">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-[14px] leading-relaxed text-[var(--cream-muted)] max-w-sm">
                      {s.desc}
                    </p>

                    <div className="mt-auto pt-8 flex items-center gap-2 text-[var(--gold)] opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                      <span className="text-[11px] uppercase tracking-[0.2em]">View Details</span>
                      <ArrowUpRight size={16} />
                    </div>
                  </div>
                </motion.article>
              );

              const getRoute = (title) => {
                const routes = {
                  "Film Making": "/services/film-making",
                  "Video Editing": "/services/video-editing",
                  "Motion Graphics": "/services/motion-graphics",
                  "Web Development": "/services/web-development",
                  "UI / UX Design": "/services/ui-ux-design",
                  "Branding": "/services/branding",
                  "Social Media": "/services/social-media",
                  // "SEO & Performance": "/services/seo-performance",
                  "Performance Marketing": "/services/performance-marketing"
                };
                return routes[title] || "#";
              };

              return (
                <Link key={s.id} to={getRoute(s.title)}>
                  {CardContent}
                </Link>
              );
            })}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

const Kicker = ({ index, label }) => (
  <div className="flex items-center gap-4">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)]">{label.toUpperCase()}</span>
  </div>
);

export default Services;
